import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Button,
  Divider,
  Typography,
  Container,
  Paper,
  Alert,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  TextField,
} from '@mui/material';
import { ContentCopy as CopyIcon } from '@mui/icons-material';
import MicrosoftIcon from '@/components/icons/MicrosoftIcon';
import { useSystemSsoStatus } from './hooks/useSystemSsoStatus';
import { API_ENDPOINTS } from '@/services/api/endpoints';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useAppSelector } from '@/store/redux';
import {
  useLogin,
  useMfaValidate,
  useMfaForcedSetupInit,
  useMfaForcedSetupConfirm,
  useApplyLoginSuccess,
  type MFASetupResponse,
} from './hooks/useAuth';
import type { User } from '@/types';
import { selectIsAuthenticated } from '@/store/redux/slices/authSlice';
import { ROUTES } from '@/routes/config';
import { FormProvider, TextBox } from '@/components/form';
import { loginSchema, type LoginFormValues } from './validations';

type Stage = 'login' | 'verify' | 'forced_setup' | 'forced_recovery';

const extractError = (err: unknown, fallback: string): string => {
  const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
  if (detail) return detail;
  if (err instanceof Error) return err.message;
  return fallback;
};

const Login: React.FC = () => {
  const navigate = useNavigate();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);

  const [mfaStage, setMfaStage] = useState<Stage>('login');
  const [mfaToken, setMfaToken] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaError, setMfaError] = useState<string | null>(null);
  const [setupQr, setSetupQr] = useState<MFASetupResponse | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [rememberDevice, setRememberDevice] = useState(false);
  const [pendingAuth, setPendingAuth] = useState<{ user: User; token: string; refreshToken: string } | null>(null);

  const methods = useForm<LoginFormValues>({
    resolver: yupResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const login = useLogin();
  const mfaValidate = useMfaValidate();
  const mfaForcedInit = useMfaForcedSetupInit();
  const mfaForcedConfirm = useMfaForcedSetupConfirm();
  const applyLoginSuccess = useApplyLoginSuccess();
  const { data: ssoStatus } = useSystemSsoStatus();

  useEffect(() => {
    if (isAuthenticated) {
      navigate(ROUTES.DASHBOARD);
    }
  }, [isAuthenticated, navigate]);

  const onSubmit = async (data: LoginFormValues) => {
    setMfaError(null);
    try {
      const result = await login.mutateAsync(data);
      if (result.status === 'mfa_required') {
        setMfaToken(result.mfaToken);
        setMfaStage('verify');
      } else if (result.status === 'mfa_setup_required') {
        setMfaToken(result.mfaToken);
        setMfaStage('forced_setup');
        try {
          const qr = await mfaForcedInit.mutateAsync(result.mfaToken);
          setSetupQr(qr);
        } catch (err) {
          setMfaError(extractError(err, 'Failed to start MFA setup'));
        }
      }
      // success: useLogin onSuccess already navigates
    } catch {
      // Error is handled by mutation.error in the UI
    }
  };

  const handleMfaSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMfaError(null);
    try {
      await mfaValidate.mutateAsync({ mfa_token: mfaToken, code: mfaCode, remember: rememberDevice });
      // success: useMfaValidate onSuccess already navigates
    } catch (err) {
      setMfaError(extractError(err, 'Invalid MFA code'));
    }
  };

  const handleForcedSetupConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setMfaError(null);
    try {
      const result = await mfaForcedConfirm.mutateAsync({ mfa_token: mfaToken, code: mfaCode });
      setPendingAuth({ user: result.user, token: result.token, refreshToken: result.refreshToken });
      setRecoveryCodes(result.recoveryCodes);
      setMfaStage('forced_recovery');
    } catch (err) {
      setMfaError(extractError(err, 'Invalid code'));
    }
  };

  const finishForcedSetup = () => {
    if (pendingAuth) {
      applyLoginSuccess(pendingAuth.user, pendingAuth.token, pendingAuth.refreshToken);
    }
  };

  const copyRecoveryCodes = () => {
    if (recoveryCodes.length) navigator.clipboard.writeText(recoveryCodes.join('\n'));
  };

  const resetToLogin = () => {
    setMfaStage('login');
    setMfaToken('');
    setMfaCode('');
    setMfaError(null);
    setSetupQr(null);
    setRecoveryCodes([]);
    setPendingAuth(null);
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 5 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            borderRadius: 0.75,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography
              sx={{
                fontWeight: 800,
                color: 'secondary.main',
                fontSize: '1.75rem',
                letterSpacing: '0.02em',
                lineHeight: 1,
              }}
            >
              VERVE VERO
            </Typography>
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mt: 1.5,
                color: 'text.primary',
                letterSpacing: '0.18em',
                fontWeight: 700,
              }}
            >
              PLATFORM ADMIN
            </Typography>
          </Box>
          {mfaStage === 'login' && (
            <>
              <Typography
                component="h1"
                variant="h4"
                sx={{ mb: 0.5, textAlign: 'center', fontWeight: 800 }}
              >
                Sign in
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
                Create new portals and manage all portal settings in one place.
              </Typography>

              {login.error && (
                <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
                  {login.error.message || 'Login failed. Please try again.'}
                </Alert>
              )}

              <FormProvider methods={methods} onSubmit={onSubmit} sx={{ width: '100%' }}>
                <TextBox
                  name="email"
                  margin="normal"
                  fullWidth
                  id="email"
                  label="Email Address"
                  autoComplete="email"
                  autoFocus
                  disabled={login.isPending}
                />
                <TextBox
                  name="password"
                  margin="normal"
                  fullWidth
                  label="Password"
                  type="password"
                  id="password"
                  autoComplete="current-password"
                  disabled={login.isPending}
                />
                <Box sx={{ textAlign: 'right', mt: 1 }}>
                  <Link to={ROUTES.FORGOT_PASSWORD} style={{ textDecoration: 'none', fontSize: '0.875rem' }}>
                    Forgot Password?
                  </Link>
                </Box>
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 2, mb: 2 }}
                  disabled={login.isPending}
                  startIcon={login.isPending ? <CircularProgress size={20} /> : null}
                >
                  {login.isPending ? 'Signing in...' : 'Sign In'}
                </Button>
              </FormProvider>

              {ssoStatus?.sso_enabled && (
                <>
                  <Divider sx={{ my: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      OR
                    </Typography>
                  </Divider>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<MicrosoftIcon />}
                    onClick={() => {
                      let base = import.meta.env.VITE_API_BASE_URL || '/api/v1/';
                      if (!base.endsWith('/')) base += '/';
                      window.location.href = `${base}${API_ENDPOINTS.SSO.SYSTEM_LOGIN}`;
                    }}
                    sx={{ mb: 1 }}
                  >
                    Sign in with Microsoft
                  </Button>
                </>
              )}
            </>
          )}

          {mfaStage === 'verify' && (
            <>
              <Typography component="h1" variant="h5" sx={{ mb: 1, textAlign: 'center', fontWeight: 700 }}>
                Two-Factor Authentication
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
                Enter the 6-digit code from your authenticator app, or a recovery code.
              </Typography>

              {mfaError && (
                <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
                  {mfaError}
                </Alert>
              )}

              <Box component="form" onSubmit={handleMfaSubmit} sx={{ width: '100%' }}>
                <TextField
                  fullWidth
                  required
                  autoFocus
                  margin="normal"
                  label="Authentication code"
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value)}
                  inputProps={{ inputMode: 'text', autoComplete: 'one-time-code' }}
                  disabled={mfaValidate.isPending}
                />
                <FormControlLabel
                  control={<Checkbox checked={rememberDevice} onChange={(e) => setRememberDevice(e.target.checked)} size="small" />}
                  label="Remember this device"
                  sx={{ mt: 1 }}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 2, mb: 1 }}
                  disabled={mfaValidate.isPending}
                  startIcon={mfaValidate.isPending ? <CircularProgress size={20} /> : null}
                >
                  {mfaValidate.isPending ? 'Verifying...' : 'Verify Code'}
                </Button>
                <Button
                  fullWidth
                  variant="text"
                  onClick={resetToLogin}
                  disabled={mfaValidate.isPending}
                >
                  Back to login
                </Button>
              </Box>
            </>
          )}

          {mfaStage === 'forced_setup' && (
            <>
              <Typography component="h1" variant="h5" sx={{ mb: 1, textAlign: 'center', fontWeight: 700 }}>
                Set up Two-Factor Authentication
              </Typography>
              <Alert severity="info" sx={{ width: '100%', mb: 2 }}>
                Your administrator requires MFA. Set it up to continue.
              </Alert>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
                Scan this QR code with an authenticator app, then enter the 6-digit code below.
              </Typography>

              {mfaForcedInit.isPending && !setupQr ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : setupQr ? (
                <>
                  <Box
                    component="img"
                    src={setupQr.qr_code}
                    alt="MFA QR code"
                    sx={{
                      width: 180,
                      height: 180,
                      display: 'block',
                      mb: 1.5,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      bgcolor: 'background.paper',
                    }}
                  />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', textAlign: 'center', mb: 2, wordBreak: 'break-all' }}
                  >
                    Or enter this secret manually: <code>{setupQr.secret}</code>
                  </Typography>
                </>
              ) : null}

              {mfaError && (
                <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
                  {mfaError}
                </Alert>
              )}

              <Box component="form" onSubmit={handleForcedSetupConfirm} sx={{ width: '100%' }}>
                <TextField
                  fullWidth
                  required
                  autoFocus
                  margin="normal"
                  label="6-digit code"
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value)}
                  inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 6 }}
                  disabled={!setupQr || mfaForcedConfirm.isPending}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 2, mb: 1 }}
                  disabled={!setupQr || mfaForcedConfirm.isPending || mfaCode.length < 6}
                  startIcon={mfaForcedConfirm.isPending ? <CircularProgress size={20} /> : null}
                >
                  {mfaForcedConfirm.isPending ? 'Verifying...' : 'Verify & Continue'}
                </Button>
                <Button
                  fullWidth
                  variant="text"
                  onClick={resetToLogin}
                  disabled={mfaForcedConfirm.isPending}
                >
                  Back to login
                </Button>
              </Box>
            </>
          )}

          {mfaStage === 'forced_recovery' && (
            <>
              <Typography component="h1" variant="h5" sx={{ mb: 1, textAlign: 'center', fontWeight: 700 }}>
                Save your recovery codes
              </Typography>
              <Alert severity="warning" sx={{ width: '100%', mb: 2 }}>
                Save these codes in a safe place. Each code can be used once if you lose access
                to your authenticator. They will not be shown again.
              </Alert>
              <Box
                sx={{
                  width: '100%',
                  p: 2,
                  mb: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  fontFamily: 'monospace',
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                }}
              >
                <Box>
                  {recoveryCodes.map((rc) => (
                    <Typography key={rc} sx={{ fontFamily: 'monospace' }}>
                      {rc}
                    </Typography>
                  ))}
                </Box>
                <IconButton size="small" onClick={copyRecoveryCodes} title="Copy">
                  <CopyIcon fontSize="small" />
                </IconButton>
              </Box>
              <Button fullWidth variant="contained" onClick={finishForcedSetup}>
                I&apos;ve saved my codes — Continue
              </Button>
            </>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default Login;
